/**
 * streamingFlag — the cross-tab "an answer is writing" lease.
 *
 * Every surface that must not act over a live answer asks ONE question,
 * `isAnyTabStreaming()`: the sync/hydration guards defer storage writes over
 * an in-memory streaming message, and the app-update banner waits to offer
 * "refresh now" until no answer is mid-flight.
 *
 * DESIGN (v3, 2026-09-02 — the "greyed-out refresh" production report):
 *
 *   Each TAB owns exactly one localStorage key, `<prefix><tabId>` → `<ts>`,
 *   written while THIS tab has a turn in flight and heartbeat-renewed every
 *   HEARTBEAT_MS. Readers scan the prefix: any key stamped within
 *   FRESHNESS_MS means some tab is mid-answer. A tab that reloads, closes,
 *   or crashes stops renewing and its key goes stale in ~1.5 minutes (pagehide
 *   removes it immediately in the normal case). No counts, no arithmetic
 *   across tabs, nothing to drift.
 *
 *   Whether THIS tab holds a lease is DERIVED, not counted: useGlobalChat
 *   mirrors "any thread processing OR any send queued" into
 *   `setLocalStreamingActive()`. The per-thread processing flag is the same
 *   signal that gates the composer and drains the queue — it is watchdog-
 *   released on dead turns and cleared by every terminal path — so the lease
 *   is exactly as honest as the send path itself.
 *
 * HISTORY — why the refcount is gone:
 *   v1 (a bare timestamp) was removed on ANY finalize, so one thread finishing
 *   dropped another's guard. v2 (2026-07/08) refcounted `count:ts` with a
 *   balanced +1 on every streaming-message start and −1 on finalize — but
 *   placeholders (thinking widgets, tool cards, tool-result bubbles) start
 *   through the same path and are REMOVED, never finalized, so an ordinary
 *   turn leaked at least two increments, the tab-local heartbeat renewed the
 *   orphaned count every 20s for the life of the tab, and the "silence"
 *   forfeit never fired because a decrement counted as activity. Net effect:
 *   after the first message in a tab, the update banner's CTA read "waits for
 *   your answer…" until the 10-minute fail-open — the report this rewrite
 *   answers. The v2 header claimed to have killed exactly this wedge; it had
 *   moved it from the stored timestamp into the tab-local silence clock.
 *
 *   The legacy shared key (STREAMING_FLAG_KEY) is never written by v3 and is
 *   swept when stale; a still-running v2 tab's fresh legacy key is IGNORED
 *   by v3 readers on purpose — honoring it would re-import the wedge into
 *   every fresh tab for as long as one old tab stayed open.
 */

/** Legacy (v1/v2) shared key — swept when stale, never written, never read for decisions. */
export const STREAMING_FLAG_KEY = 'prest0_global_chat_streaming';

/** Per-tab lease keys: `${STREAMING_LEASE_KEY_PREFIX}${tabId}` → `${timestampMs}`. */
export const STREAMING_LEASE_KEY_PREFIX = 'prest0_global_chat_streaming:';

/**
 * sessionStorage slot holding the PREVIOUS boot's tab id. Every boot mints a
 * fresh id (a browsing context is unique; the id must be too — "Duplicate
 * tab" copies sessionStorage verbatim, so a persisted id would let two live
 * tabs share one lease key and the duplicate's boot would delete the
 * original's live lease). The stored id is read back for exactly one thing:
 * on a RELOAD navigation, retiring the predecessor's key immediately instead
 * of waiting out the freshness window.
 */
const TAB_ID_SESSION_KEY = 'prest0_streaming_tab_id';

/** Lease renewal cadence while this tab has a live turn. */
export const STREAMING_FLAG_HEARTBEAT_MS = 20_000;

/**
 * How long readers trust the last stamp. Several missed heartbeats plus
 * margin — a crashed or closed tab's leftover clears in this long, worst case.
 */
export const STREAMING_FLAG_FRESHNESS_MS = 90_000;

/**
 * Belt over the derived signal: a lease with no chunk, no start, and no
 * activation for this long is forfeited even if the processing flag is
 * somehow still up. Longest legitimate silence is a deep-research stage
 * wait (3-5 min); the task-backed lane ticks its placeholder every second,
 * so 10 minutes of true silence means something is wedged.
 */
export const STREAMING_FLAG_MAX_SILENCE_MS = 600_000;

const REFRESH_MIN_INTERVAL_MS = 1_000;

// ── Tab identity ─────────────────────────────────────────────────────────────

function randomTabId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** True when this page load is a reload of the same browsing context. */
function isReloadNavigation(): boolean {
  try {
    const entries = performance.getEntriesByType('navigation') as Array<{ type?: string }>;
    return entries?.[0]?.type === 'reload';
  } catch {
    return false;
  }
}

/**
 * Boot: mint this context's id, remember it for the next boot, and — only
 * when this boot IS a reload — retire the predecessor's lease key (a reload
 * kills any stream that tab had; a duplicated or freshly opened tab must not
 * touch a key that may still be another live tab's).
 */
function bootTabId(): string {
  const fresh = randomTabId();
  try {
    const previous = sessionStorage.getItem(TAB_ID_SESSION_KEY);
    if (previous && isReloadNavigation()) {
      try { localStorage.removeItem(`${STREAMING_LEASE_KEY_PREFIX}${previous}`); } catch { /* ignore */ }
    }
    sessionStorage.setItem(TAB_ID_SESSION_KEY, fresh);
  } catch { /* ignore */ }
  return fresh;
}

let tabId = typeof window !== 'undefined' ? bootTabId() : randomTabId();

/** This tab's lease key. Exported for tests and diagnostics. */
export function ownStreamingLeaseKey(): string {
  return `${STREAMING_LEASE_KEY_PREFIX}${tabId}`;
}

// ── Readers ──────────────────────────────────────────────────────────────────

/** A lease value is fresh when its stamp is within the freshness window. */
export function isStreamingLeaseFresh(raw: string | null, nowMs: number = Date.now()): boolean {
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  if (!Number.isFinite(ts)) return false;
  return nowMs - ts < STREAMING_FLAG_FRESHNESS_MS;
}

/** Every per-tab lease key currently in storage (own included). */
function leaseKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STREAMING_LEASE_KEY_PREFIX)) keys.push(k);
    }
  } catch { /* ignore */ }
  return keys;
}

/** The one question every reader asks. Storage errors read as "not streaming". */
export function isAnyTabStreaming(nowMs: number = Date.now()): boolean {
  try {
    for (const k of leaseKeys()) {
      if (isStreamingLeaseFresh(localStorage.getItem(k), nowMs)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ── Tab-local lease state ────────────────────────────────────────────────────

let localActive = false;
let localLastActivityMs = 0;
let lastRefreshAt = 0;
let heartbeatId: ReturnType<typeof setInterval> | null = null;

function writeOwnLease(nowMs: number): void {
  try { localStorage.setItem(ownStreamingLeaseKey(), String(nowMs)); } catch { /* ignore */ }
}

function removeOwnLease(): void {
  try { localStorage.removeItem(ownStreamingLeaseKey()); } catch { /* ignore */ }
}

function heartbeatTick(): void {
  const nowMs = Date.now();
  if (nowMs - localLastActivityMs > STREAMING_FLAG_MAX_SILENCE_MS) {
    // Nothing on this tab's turn moved for far longer than any legitimate
    // stage wait: forfeit rather than renew a lease nobody is using.
    localActive = false;
    removeOwnLease();
    syncHeartbeat();
    return;
  }
  writeOwnLease(nowMs);
}

function syncHeartbeat(): void {
  if (localActive && heartbeatId === null) {
    heartbeatId = setInterval(heartbeatTick, STREAMING_FLAG_HEARTBEAT_MS);
  } else if (!localActive && heartbeatId !== null) {
    clearInterval(heartbeatId);
    heartbeatId = null;
  }
}

/**
 * Mirror the tab's derived "turn in flight" truth onto the lease. Called from
 * an effect over `processingByThread` + `pendingByThread` — every transition,
 * idempotent on repeats. `false` releases immediately.
 */
export function setLocalStreamingActive(active: boolean): void {
  if (active) {
    if (!localActive) {
      localActive = true;
      localLastActivityMs = Date.now();
      writeOwnLease(localLastActivityMs);
    }
  } else if (localActive) {
    localActive = false;
    removeOwnLease();
  }
  syncHeartbeat();
}

/**
 * Synchronous start hook for the streaming-message creators: the lease must
 * be visible BEFORE the placeholder is dispatched, or the same-tab sync poll
 * can fire SET_MESSAGES over it in the gap before the derived effect runs.
 * The effect confirms (or, if the turn already ended, releases) afterwards.
 */
export function noteStreamStarted(): void {
  localActive = true;
  localLastActivityMs = Date.now();
  writeOwnLease(localLastActivityMs);
  syncHeartbeat();
}

/**
 * Chunk-driven lease renewal, throttled to once a second (this runs on every
 * streamed-message update; the paced reveal can call it per animation frame).
 * A tab with no live turn never writes.
 */
export function refreshStreamingFlag(): void {
  if (!localActive) return;
  const nowMs = Date.now();
  localLastActivityMs = nowMs;
  if (nowMs - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) return;
  lastRefreshAt = nowMs;
  writeOwnLease(nowMs);
}

/** Parse the legacy `count:ts` / `ts` value to its timestamp (sweep only). */
function legacyStampMs(raw: string | null): number | null {
  if (!raw) return null;
  const idx = raw.indexOf(':');
  const ts = parseInt(idx === -1 ? raw : raw.slice(idx + 1), 10);
  return Number.isFinite(ts) ? ts : null;
}

/**
 * Mount-time hygiene: drop STALE leases (other tabs that died without a
 * pagehide) and a stale legacy key. Fresh keys are left alone — they are
 * live tabs. Own key is left to the derived signal.
 */
export function sweepStaleStreamingFlag(nowMs: number = Date.now()): void {
  try {
    const own = ownStreamingLeaseKey();
    for (const k of leaseKeys()) {
      if (k === own) continue;
      if (!isStreamingLeaseFresh(localStorage.getItem(k), nowMs)) localStorage.removeItem(k);
    }
    const legacy = localStorage.getItem(STREAMING_FLAG_KEY);
    if (legacy !== null) {
      const ts = legacyStampMs(legacy);
      if (ts === null || nowMs - ts >= STREAMING_FLAG_FRESHNESS_MS) localStorage.removeItem(STREAMING_FLAG_KEY);
    }
  } catch { /* ignore */ }
}

// pagehide releases this tab's key on the way out (reload, close, navigate
// away); a reload's boot additionally retires the predecessor key (see
// bootTabId) so a crash-then-reload never leaves a 90s ghost. If the page
// comes back from bfcache mid-turn, the heartbeat re-establishes the lease.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', removeOwnLease);
}

/** Test-only: reset module state between cases. */
export function __resetStreamingFlagForTests(nextTabId?: string): void {
  removeOwnLease();
  localActive = false;
  localLastActivityMs = 0;
  lastRefreshAt = 0;
  if (heartbeatId !== null) {
    clearInterval(heartbeatId);
    heartbeatId = null;
  }
  if (nextTabId) tabId = nextTabId;
}
